/**
 * AppShell — the ShellLayout from Operations App.tsx: navy rail (collapsible
 * on desktop, a drawer under lg), white top bar, page-colored content area.
 * The outer frame is the viewport height so the board's columns can own
 * their vertical scroll; `<main>` scrolls everything else.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { useFocusTrap } from '@/lib/focus-trap';
import { useShellRouteSync } from '@/lib/shell-bridge';
import { useShopBootstrap } from '@/lib/shop-store';

const LS_RAIL = 'recruiting.shell.railCollapsed';

function readRailCollapsed(): boolean {
  try {
    return localStorage.getItem(LS_RAIL) === '1';
  } catch {
    return false;
  }
}

export function AppShell({ children }: { children: ReactNode }) {
  useShopBootstrap();
  useShellRouteSync();

  // Mobile nav: the sidebar hides under lg and opens as a drawer from the
  // TopBar hamburger; tapping a link, the backdrop or Escape closes it.
  const [navOpen, setNavOpen] = useState(false);
  const closeNav = useCallback(() => setNavOpen(false), []);
  const drawerRef = useRef<HTMLDivElement>(null);
  useFocusTrap(drawerRef, { active: navOpen, onEscape: closeNav });

  // Desktop: collapsing the rail gives the board ~200px back. Per browser;
  // the read is guarded because private windows throw on access.
  const [railCollapsed, setRailCollapsed] = useState(readRailCollapsed);
  useEffect(() => {
    try {
      localStorage.setItem(LS_RAIL, railCollapsed ? '1' : '0');
    } catch {
      // private mode — the choice simply does not survive a reload
    }
  }, [railCollapsed]);

  return (
    <div className="flex h-dvh bg-page text-gray-900 overflow-hidden">
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      {!railCollapsed && (
        <div className="hidden lg:flex">
          <Sidebar onCollapse={() => setRailCollapsed(true)} />
        </div>
      )}
      {railCollapsed && (
        <button
          type="button"
          onClick={() => setRailCollapsed(false)}
          aria-label="Show sidebar"
          title="Show sidebar"
          className="hidden lg:flex fixed left-0 top-3 z-40 h-10 w-10 items-center justify-center rounded-r-lg border border-l-0 border-gray-200 bg-white text-gray-500 shadow-sm hover:bg-gray-100 hover:text-gray-900"
        >
          <ChevronRight size={14} strokeWidth={2.4} aria-hidden />
        </button>
      )}
      {navOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="nav-drawer">
          <div className="absolute inset-0 bg-black/50" data-testid="nav-backdrop" onClick={closeNav} aria-hidden />
          <div
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
            tabIndex={-1}
            className="absolute inset-y-0 left-0 flex shadow-2xl outline-none overscroll-contain"
          >
            <Sidebar onNavigate={closeNav} />
          </div>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onMenuClick={() => setNavOpen(true)} />
        <main id="main" tabIndex={-1} className="grid-bg min-h-0 flex-1 overflow-auto outline-none">
          {children}
        </main>
      </div>
    </div>
  );
}
